import {
  toWSDateString,
  WSActions,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSInfoBox,
  WSText,
  WSTimeline
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useQuerylistResourceEvents } from "../../../query/resourceEvents/queries/useQueryListEvents";
import { components } from "../../../services/api/resourceEvents/types";
import { eventToTimeline } from "./eventToTimeline";

type Props = {
  refetchOnMount?: boolean;
  resources: components["schemas"]["IEventResource"][];
  onViewAll?: () => void;
};

const NotBackfilledResourceTypes = [
  "Engagement",
  "EngagementRequirementDefinition",
  "RequirementDefinition",
  "Payer",
  "Payee",
  "PayerPayeeEngagement",
  "Requirement"
];

const HISTORY_TRACKING_START_DATE = "7-11-2025";

export const ResourceHistory: React.FC<Props> = ({
  refetchOnMount = true,
  ...props
}) => {
  const queryResourceEvents = useQuerylistResourceEvents(
    {
      filter: {
        resource: {
          data: props.resources
        }
      },
      "page[size]": !!props.onViewAll ? 4 : 100
    },
    {
      refetchOnMount
    }
  );

  const showNotAllBackfilledNote = useMemo(() => {
    return props.resources.some(resource =>
      NotBackfilledResourceTypes.includes(resource.resourceType)
    );
  }, [props.resources]);

  const resourceEvents = useMemo(
    () =>
      queryResourceEvents.data?.map(event =>
        eventToTimeline(event, !!props.onViewAll)
      ) || [],
    [queryResourceEvents.data, props.onViewAll]
  );

  const timelineItems = useMemo(() => {
    const items = [...resourceEvents];
    if (items.length === 4 && !!props.onViewAll) {
      items.push({
        content: (
          <WSButton.Link onClick={props.onViewAll}>View all</WSButton.Link>
        ),
        avatar: {
          type: "icon",
          icon: "time"
        }
      });
    }
    return items;
  }, [resourceEvents, props.onViewAll]);

  return (
    <WSElement>
      <WSFlexBox
        alignItems="center"
        justify="space-between"
        wrap="nowrap"
        mb="M"
      >
        <WSText weight="medium">
          {props.onViewAll ? "Recent history" : "History"}
        </WSText>
        {props.onViewAll && queryResourceEvents.isSuccess && (
          <WSActions
            buttons={[
              { kind: "Link", label: "View all", onClick: props.onViewAll }
            ]}
          />
        )}
      </WSFlexBox>

      <WSCard>
        {queryResourceEvents.isSuccess ? (
          timelineItems.length ? (
            <WSTimeline items={timelineItems} />
          ) : (
            <WSFlexBox.Center mb="L">
              <WSText.ParagraphSm color="gray400">
                No events yet
              </WSText.ParagraphSm>
            </WSFlexBox.Center>
          )
        ) : (
          <WSElement shimmer pb="2XL">
            Loading history...
          </WSElement>
        )}
        {showNotAllBackfilledNote ? (
          <WSInfoBox mt="M">
            <WSText.ParagraphXs color="gray500">
              Note: some history events tracking is only available from this
              date onward:{" "}
              {toWSDateString(HISTORY_TRACKING_START_DATE, "monthDayYear")}.
            </WSText.ParagraphXs>
          </WSInfoBox>
        ) : null}
      </WSCard>
    </WSElement>
  );
};
